import { useState, useCallback, useEffect, useRef, startTransition } from 'react';
import { markIntentionalEmptyTasksSave } from '../../../../utils/allowEmptyTasksSave';
import { getNodeDimensions } from './utils/canvasUtils';
import {
  clearSelectedNodeClasses,
  applySelectedNodeClass,
  toggleSelectedNodeClass,
  selectAllFlowchartNodeClasses,
} from './utils/nodeSelectionDom';
import { clearFlowchartTextSelections } from './utils/clearFlowchartTextSelections';
import { findTaskElement } from '../cardBitmaps/findTaskElement';
import { removeTaskFromConditions, normalizeToV2, hasAnyConditions, hasConditionForTask } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Hook for managing flowchart selection state (nodes, edges, selection box)
 * 
 * MIGRATION: Now uses onConditionsChange to delete edges from unlockConditions.
 * Falls back to onDependencyChange for tasks still using the old dependsOn system.
 * 
 * PERFORMANCE: Selection box uses refs during drag to avoid React re-renders,
 * only syncing state at the end when selection is complete.
 * 
 * @param {Object} params
 * @param {Array} params.tasks - All tasks
 * @param {Object} params.nodePositions - Base node positions
 * @param {Object} params.customPositions - Custom/dragged positions
 * @param {Object} params.nodeDimensions - Node dimensions
 * @param {Function} params.screenToCanvas - Convert screen coords to canvas coords
 * @param {Function} params.onConditionsChange - Callback for conditions changes (new system)
 * @param {Function} params.onDependencyChange - Callback for dependency changes (legacy)
 * @param {Function} params.onDeleteTasks - Callback for bulk delete
 * @param {React.RefObject} params.effectivePositionsRef - Optional ref to positions used for rendering
 *        (e.g., focus mode positions). If provided, selection box will use these instead.
 * @param {Function} params.onAfterDelete - Optional callback called after nodes are deleted (receives deleted IDs)
 */
export function useFlowchartSelection({
  tasks,
  nodePositions,
  customPositions,
  nodeDimensions,
  screenToCanvas,
  onConditionsChange,
  onDependencyChange,
  onDeleteTasks, // Bulk delete function (no confirmation)
  effectivePositionsRef = null, // Ref to positions actually used for rendering (for focus mode)
  onAfterDelete = null, // Called after deletion with the deleted task IDs
  onEdgeDeleted = null, // Called after a connection line is removed — triggers auto-tidy
  campaignId = '',
  campaignName = '',
}) {
  // Selection state
  const [selectedNodes, setSelectedNodesRaw] = useState(new Set());
  const [selectedEdge, setSelectedEdge] = useState(null);
  
  // PERFORMANCE: Wrap setSelectedNodes in startTransition to make updates non-blocking
  // This allows the browser to remain responsive during the expensive re-render
  const setSelectedNodes = useCallback((newSelection) => {
    startTransition(() => {
      setSelectedNodesRaw(newSelection);
    });
  }, []);
  
  // Selection box state (right-click drag)
  const [selectionBox, setSelectionBox] = useState(null);
  const [isSelectingBox, setIsSelectingBox] = useState(false);
  const [selectionBoxStart, setSelectionBoxStart] = useState({ x: 0, y: 0 });
  
  // PERFORMANCE: Refs for direct DOM manipulation during selection box drag
  const selectionBoxRef = useRef(null); // Current box dimensions (for DOM updates)
  const selectionBoxStartRef = useRef({ x: 0, y: 0 }); // Start position
  const selectionBoxElementRef = useRef(null); // Cached DOM element

  // Delete selected nodes (bulk operation). Delete-all requires confirmation via modal.
  const performDeleteSelected = useCallback(() => {
    if (selectedNodes.size === 0 || !onDeleteTasks) return;

    const deletedIds = Array.from(selectedNodes);
    onDeleteTasks(deletedIds);
    setSelectedNodes(new Set());

    if (onAfterDelete) {
      setTimeout(() => onAfterDelete(deletedIds), 50);
    }
  }, [selectedNodes, onDeleteTasks, onAfterDelete, setSelectedNodes]);

  const deleteSelectedNodes = useCallback(() => {
    if (selectedNodes.size === 0 || !onDeleteTasks) return;

    const isDeleteAll = tasks.length > 0 && selectedNodes.size === tasks.length;

    if (isDeleteAll) {
      window.dispatchEvent(new CustomEvent('fokus:requestDeleteAllTasks', {
        detail: {
          campaignId,
          campaignName: campaignName || 'this campaign',
          taskCount: tasks.length,
          onConfirm: () => {
            markIntentionalEmptyTasksSave(campaignId);
            performDeleteSelected();
          },
        },
      }));
      return;
    }

    performDeleteSelected();
  }, [selectedNodes, onDeleteTasks, tasks.length, campaignId, campaignName, performDeleteSelected]);

  /**
   * Remove a task from unlockConditions (handles v1, v2, and dependsOn migration)
   */
  const removeFromConditions = useCallback((targetTaskId, sourceTaskId) => {
    const task = tasks.find(t => t.id === targetTaskId);
    if (!task) return false;

    let conditions = task.unlockConditions;
    if (!hasAnyConditions(conditions) && Array.isArray(task.dependsOn) && task.dependsOn.length > 0) {
      conditions = normalizeToV2(task.dependsOn.map((dep, i) => ({
        taskId: typeof dep === 'string' ? dep : dep?.taskId,
        branch: typeof dep === 'object' ? dep?.branch : undefined,
        operator: i === 0 ? null : 'AND',
      })).filter(c => c.taskId));
    }

    if (!hasConditionForTask(conditions, sourceTaskId)) return false;

    const updated = removeTaskFromConditions(conditions, sourceTaskId);
    onConditionsChange?.(targetTaskId, updated);
    return true;
  }, [tasks, onConditionsChange]);

  /**
   * Remove a task from dependsOn (legacy)
   */
  const removeFromDependsOn = useCallback((targetTaskId, sourceTaskId) => {
    const task = tasks.find(t => t.id === targetTaskId);
    if (!task) return false;
    
    const currentDeps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
    
    // Filter out the dependency
    const filteredDeps = currentDeps.filter(dep => {
      const depId = typeof dep === 'string' ? dep : dep?.taskId;
      return depId !== sourceTaskId;
    });
    
    if (filteredDeps.length !== currentDeps.length) {
      onDependencyChange?.(targetTaskId, filteredDeps);
      return true;
    }
    return false;
  }, [tasks, onDependencyChange]);

  // Handle keyboard events for selection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // Delete selected edge
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedEdge) {
        e.preventDefault();
        
        // Always use the new conditions system (it will migrate dependsOn if needed)
        const removed = removeFromConditions(selectedEdge.to, selectedEdge.from);
        
        setSelectedEdge(null);
        if (removed) onEdgeDeleted?.();
        return;
      }
      
      // Delete selected nodes
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodes.size > 0) {
        e.preventDefault();
        deleteSelectedNodes();
        return;
      }
      
      // Escape clears selection
      if (e.key === 'Escape') {
        // PERFORMANCE: Immediately remove selection classes via DOM
        clearSelectedNodeClasses();
        document.querySelectorAll('.flowchart__edge--selected').forEach(el => {
          el.classList.remove('flowchart__edge--selected');
        });
        const toolbar = document.querySelector('.bulk-actions-toolbar');
        if (toolbar) toolbar.style.display = 'none';
        
        setSelectedEdge(null);
        setSelectedNodes(new Set());
        setIsSelectingBox(false);
        setSelectionBox(null);
      }
      
      // Select all with Ctrl/Cmd + A
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        // PERFORMANCE: Immediately add selection classes via DOM
        selectAllFlowchartNodeClasses();
        // Reset toolbar display in case it was hidden
        const toolbar = document.querySelector('.bulk-actions-toolbar');
        if (toolbar) toolbar.style.display = '';
        
        setSelectedNodes(new Set(tasks.map(t => t.id)));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdge, selectedNodes, tasks, removeFromConditions, removeFromDependsOn, deleteSelectedNodes, onEdgeDeleted]);

  // Start selection box
  const startSelectionBox = useCallback((e) => {
    // Blur editable fields and collapse title text selection before box select
    clearFlowchartTextSelections();
    
    const canvasPos = screenToCanvas(e.clientX, e.clientY);
    const initialBox = { x: canvasPos.x, y: canvasPos.y, width: 0, height: 0 };
    
    // Store in refs for direct DOM updates
    selectionBoxStartRef.current = canvasPos;
    selectionBoxRef.current = initialBox;
    selectionBoxElementRef.current = null; // Will be found on first update
    
    // Set React state to show the box
    setIsSelectingBox(true);
    setSelectionBoxStart(canvasPos);
    setSelectionBox(initialBox);
  }, [screenToCanvas]);

  // Update selection box during mouse move
  // PERFORMANCE: Uses direct DOM manipulation to avoid React re-renders during drag
  const updateSelectionBox = useCallback((canvasPos) => {
    if (!isSelectingBox) return false;
    
    const start = selectionBoxStartRef.current;
    const startX = Math.min(start.x, canvasPos.x);
    const startY = Math.min(start.y, canvasPos.y);
    const endX = Math.max(start.x, canvasPos.x);
    const endY = Math.max(start.y, canvasPos.y);
    
    const newBox = {
      x: startX,
      y: startY,
      width: endX - startX,
      height: endY - startY,
    };
    
    // Store in ref
    selectionBoxRef.current = newBox;
    
    // PERFORMANCE: Update DOM directly instead of React state
    // Find the selection box element if not cached
    if (!selectionBoxElementRef.current) {
      selectionBoxElementRef.current = document.querySelector('.flowchart__selection-box');
    }
    
    const boxEl = selectionBoxElementRef.current;
    if (boxEl) {
      boxEl.style.left = `${newBox.x}px`;
      boxEl.style.top = `${newBox.y}px`;
      boxEl.style.width = `${newBox.width}px`;
      boxEl.style.height = `${newBox.height}px`;
    }
    
    return true;
  }, [isSelectingBox]);

  // Complete selection box
  const completeSelectionBox = useCallback((e) => {
    if (!isSelectingBox) return false;
    
    // Use the ref for final box dimensions (more accurate than state during fast drag)
    const finalBox = selectionBoxRef.current || selectionBox;
    if (!finalBox) return false;
    
    // Use effective positions (e.g., focus mode positions) if available, otherwise fall back to standard positions
    const effectivePositions = effectivePositionsRef?.current;
    const useEffective = effectivePositions && Object.keys(effectivePositions).length > 0;
    
    // Find all nodes within the selection box
    const selected = new Set();
    tasks.forEach(task => {
      // Use effective positions (focus mode) if available, otherwise use standard positions
      const pos = useEffective 
        ? effectivePositions[task.id] 
        : (customPositions[task.id] || nodePositions[task.id]);
      
      if (!pos) return;
      
      const dims = getNodeDimensions(task.id, nodeDimensions);
      const nodeRight = pos.x + dims.width;
      const nodeBottom = pos.y + dims.height;
      const boxRight = finalBox.x + finalBox.width;
      const boxBottom = finalBox.y + finalBox.height;
      
      // Check if node intersects with selection box
      if (pos.x < boxRight && nodeRight > finalBox.x &&
          pos.y < boxBottom && nodeBottom > finalBox.y) {
        selected.add(task.id);
      }
    });
    
    // PERFORMANCE: Immediately add selection classes via DOM for instant visual feedback
    // This makes selection appear instant even with many nodes
    if (!e.shiftKey) {
      clearSelectedNodeClasses();
    }
    selected.forEach((taskId) => {
      applySelectedNodeClass(findTaskElement(taskId));
    });
    
    // Reset toolbar display (in case it was hidden by deselection)
    // React will handle showing/hiding it properly, but we need to clear the inline style
    const toolbar = document.querySelector('.bulk-actions-toolbar');
    if (toolbar) {
      toolbar.style.display = '';
    }
    
    // Then update React state (will re-render but visual is already correct)
    if (e.shiftKey) {
      setSelectedNodes(prev => new Set([...prev, ...selected]));
    } else {
      setSelectedNodes(selected);
    }
    
    // Clear refs
    selectionBoxRef.current = null;
    selectionBoxElementRef.current = null;
    
    setIsSelectingBox(false);
    setSelectionBox(null);
    return true;
  }, [isSelectingBox, selectionBox, tasks, customPositions, nodePositions, nodeDimensions, effectivePositionsRef]);

  // Handle node selection (click)
  const selectNode = useCallback((taskId, shiftKey) => {
    clearFlowchartTextSelections();
    const nodeEl = findTaskElement(taskId);

    if (shiftKey) {
      toggleSelectedNodeClass(nodeEl);
      setSelectedNodes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(taskId)) {
          newSet.delete(taskId);
        } else {
          newSet.add(taskId);
        }
        return newSet;
      });
    } else {
      clearSelectedNodeClasses();
      applySelectedNodeClass(nodeEl);
      const toolbar = document.querySelector('.bulk-actions-toolbar');
      if (toolbar) toolbar.style.display = '';
      setSelectedNodes(new Set([taskId]));
    }
    setSelectedEdge(null);
  }, []);

  // Handle edge click
  const handleEdgeClick = useCallback((edge, e) => {
    e.stopPropagation();
    clearFlowchartTextSelections();
    // PERFORMANCE: Immediately update DOM for instant visual feedback
    clearSelectedNodeClasses();
    const toolbar = document.querySelector('.bulk-actions-toolbar');
    if (toolbar) toolbar.style.display = 'none';
    
    setSelectedEdge(edge);
    setSelectedNodes(new Set());
  }, []);

  // Clear selection
  const clearSelection = useCallback((clearNodes = true) => {
    clearFlowchartTextSelections();
    // PERFORMANCE: Immediately remove selection classes via DOM
    if (clearNodes) {
      clearSelectedNodeClasses();
      const toolbar = document.querySelector('.bulk-actions-toolbar');
      if (toolbar) toolbar.style.display = 'none';
      setSelectedNodes(new Set());
    }
    document.querySelectorAll('.flowchart__edge--selected').forEach(el => {
      el.classList.remove('flowchart__edge--selected');
    });
    setSelectedEdge(null);
  }, []);

  // Mission card title Enter — deselect the card after committing the name
  useEffect(() => {
    const handleTitleEnter = () => {
      clearSelection();
    };
    window.addEventListener('missionCardTitleEnter', handleTitleEnter);
    return () => window.removeEventListener('missionCardTitleEnter', handleTitleEnter);
  }, [clearSelection]);

  // For backwards compatibility, expose selectedNode as first selected
  const selectedNode = selectedNodes.size === 1 ? [...selectedNodes][0] : null;

  return {
    // State
    selectedNode,
    selectedNodes,
    setSelectedNodes,
    selectedEdge,
    setSelectedEdge,
    selectionBox,
    isSelectingBox,
    
    // Handlers
    startSelectionBox,
    updateSelectionBox,
    completeSelectionBox,
    selectNode,
    handleEdgeClick,
    clearSelection,
    deleteSelectedNodes,
  };
}
